import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { AuthStatus } from 'constants/auth';
import { HOME_PAGE, getProfilePageUrl } from 'constants/pages';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import LoadingSkeleton from 'components/LoadingSkeleton';
import ModalShare from 'components/modals/ModalShare';
import ModalSubmitPlayerReview from 'components/modals/ModalSubmitPlayerReview';
import Head from 'components/utilities/Head';
import classNames from 'styles/utils/classNames';
import {
  ProfileAboutMe,
  ProfileActions,
  ProfileCover,
  ProfileExperience,
  ProfileHero,
  ProfilePageHeader,
  ProfilePhotoGallery,
  ProfilePreferredCourts,
  ProfilePreferredTime,
  ProfileReviews,
  ProfileStats,
} from './components';
import {
  AboutMeFormModal,
  PhotosFormModal,
  PlayingLevelFormModal,
  PreferredCourtsFormModal,
  PreferredTimeFormModal,
  ProfileFormModalName,
  SocialLinksFormModal,
  VerificationFormModal,
} from './formModals';
import { usePlayerProfile } from './usePlayerProfile';
import { usePlayerProfileEditor } from './usePlayerProfileEditor';
import { useProfileImageUploads } from './useProfileImageUploads';

interface Props {
  username: string;
  /** Seeded from getStaticProps so the shell renders before the query resolves. */
  seedName?: string | null;
  /**
   * Rendered inside another page (the You page's "Public profile" tab), which
   * already supplies the document head, cover, hero and back control. Only the
   * profile body renders.
   */
  isEmbedded?: boolean;
}

/**
 * The design's desktop measurements: a 700px content column, a 40px gutter and a
 * 340px rail. The page cap adds the container's own 24px padding back on so the
 * column lands on 700px rather than 652px.
 */
const CONTENT_WIDTH = 'max-w-[43.75rem]';
const PAGE_WIDTH = 'max-w-[70.5rem]';

function Divider({ className }: { className?: string }) {
  return (
    <hr
      className={classNames(
        'border-color-border-card-lightmode dark:border-color-border-card-darkmode',
        className,
      )}
    />
  );
}

export default function PlayerProfile({ username, seedName, isEmbedded = false }: Props) {
  const router = useRouter();
  const {
    profile,
    isLoading,
    isViewerProfile,
    isFollowing,
    isFollowLoading,
    toggleFollow,
    canWriteReview,
    refetch,
  } = usePlayerProfile(username);
  const { isOpen: isShareOpen, openModal: openShare, closeModal: closeShare } = useModal();
  const { isOpen: isReviewOpen, openModal: openReview, closeModal: closeReview } = useModal();
  const viewer = useViewer();

  // Next's history `idx` is 0 on the first entry, so there's nothing to go back to.
  const [hasHistory, setHasHistory] = useState(false);
  useEffect(() => {
    setHasHistory((window.history.state?.idx ?? 0) > 0);
  }, []);

  // Signed-in viewers have no top nav here, so back is their only way out.
  const isSignedIn = viewer.status === AuthStatus.User;
  const showBack = isSignedIn || hasHistory;
  const handleBack = useCallback(() => {
    if (hasHistory) {
      router.back();
      return;
    }
    router.push(HOME_PAGE);
  }, [hasHistory, router]);

  // Every section edits in place; the questionnaire is no longer a round trip.
  const [openFormModal, setOpenFormModal] = useState<ProfileFormModalName | null>(null);
  const closeFormModal = () => setOpenFormModal(null);
  const isFormModalOpen = (name: ProfileFormModalName) => openFormModal === name;

  const editor = usePlayerProfileEditor({ isViewerProfile, onSaved: refetch });

  // Clear on open so one editor's failure doesn't greet you in the next.
  const openEditor = (name: ProfileFormModalName) => () => {
    editor.clearError();
    setOpenFormModal(name);
  };

  // Cover/avatar edit in place instead of bouncing through onboarding.
  const {
    CropperModal,
    coverInputRef,
    avatarInputRef,
    coverPreviewUrl,
    avatarPreviewUrl,
    isUploadingCover,
    isUploadingAvatar,
    openCoverPicker,
    openAvatarPicker,
    handleCoverFile,
    handleAvatarFile,
  } = useProfileImageUploads(refetch);

  const title = profile?.name || seedName || 'Player profile';
  const shareUrl = `${process.env.APP_URL}${getProfilePageUrl(profile?.username || username)}`;

  // Mirrors PlaySessionPage/ShareButton: offer the native sheet where it exists and
  // always open the modal, which carries the QR code and copy-link fallback.
  const handleShare = useCallback(() => {
    openShare();

    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator
        .share({
          title: `${title} on Bounce`,
          text: `Check out ${title}'s profile on Bounce.`,
          url: shareUrl,
        })
        // Share sheets reject on dismissal; the modal is already the fallback.
        .catch(() => {});
    }
  }, [openShare, shareUrl, title]);

  if (isLoading || !profile) {
    return (
      <>
        <Head title={title} description={`${title} on Bounce`} />
        <div className={classNames('container mx-auto flex flex-col gap-ds-lg pb-16', PAGE_WIDTH)}>
          <LoadingSkeleton height="8.75rem" />
          <LoadingSkeleton height="8.125rem" width="8.125rem" circle className="mx-auto" />
          <LoadingSkeleton height="2.5rem" count={2} />
          <LoadingSkeleton height="6.75rem" />
        </div>
      </>
    );
  }

  const actions = (
    <ProfileActions
      isViewerProfile={isViewerProfile}
      isVerified={!!profile.verificationLevel}
      onVerify={openEditor(ProfileFormModalName.Verification)}
      onPinToFavorites={toggleFollow}
      isPinned={isFollowing}
      isPinLoading={isFollowLoading}
    />
  );

  return (
    <>
      {!isEmbedded && (
        <>
          <Head
            title={title}
            description={
              profile.aboutMe?.slice(0, 160) || `${title} plays on Bounce. See their profile.`
            }
          />

          <ProfilePageHeader onShare={handleShare} showBack={showBack} onBack={handleBack} />
        </>
      )}

      <div className={classNames('container mx-auto pb-16 md:pt-ds-2xl', PAGE_WIDTH)}>
        {/* The rail only appears once the full 700 + 40 + 340 fits. The cover spans
            both columns, so it and the content column are separate grid items —
            below `xl` the wrapper reinstates itself and centres them together. */}
        <div
          className={classNames(
            !isEmbedded && 'xl:grid xl:grid-cols-[43.75rem_21.25rem] xl:gap-x-ds-3xl',
          )}
        >
          <div
            className={classNames('mx-auto w-full', CONTENT_WIDTH, !isEmbedded && 'xl:contents')}
          >
            {!isEmbedded && (
              <ProfileCover
                coverImagePath={profile.coverImagePath}
                previewImageUrl={coverPreviewUrl}
                isViewerProfile={isViewerProfile}
                onShare={handleShare}
                onEditCover={openCoverPicker}
                isUploadingCover={isUploadingCover}
                showBack={showBack}
                onBack={handleBack}
                className="xl:col-span-2"
              />
            )}

            <div className="flex flex-col gap-ds-xl">
              {!isEmbedded && (
                <ProfileHero
                  profile={profile}
                  socialLinks={profile.socialLinks}
                  avatarPreviewUrl={avatarPreviewUrl}
                  isViewerProfile={isViewerProfile}
                  onEditAvatar={openAvatarPicker}
                  isUploadingAvatar={isUploadingAvatar}
                  onEditSocialLinks={openEditor(ProfileFormModalName.SocialLinks)}
                />
              )}

              {/* Full width on mobile, hugging the left edge once the hero left-aligns.
                  Embedded, the You page header already carries these actions. */}
              {!isEmbedded && (
                <div className="sm:mx-auto sm:w-full sm:max-w-[26rem] md:mx-0 xl:hidden">
                  {actions}
                </div>
              )}

              <ProfileStats stats={profile.stats} />

              <ProfilePhotoGallery
                photos={profile.photos}
                isViewerProfile={isViewerProfile}
                onEdit={openEditor(ProfileFormModalName.Photos)}
              />

              <Divider />

              <ProfileAboutMe
                profile={profile}
                isViewerProfile={isViewerProfile}
                onEdit={openEditor(ProfileFormModalName.AboutMe)}
              />

              <ProfileExperience
                experiences={profile.experiences}
                isViewerProfile={isViewerProfile}
                onEdit={openEditor(ProfileFormModalName.PlayingLevel)}
              />

              <ProfilePreferredTime
                availability={profile.availability}
                isViewerProfile={isViewerProfile}
                onEdit={openEditor(ProfileFormModalName.PreferredTime)}
              />

              <Divider />

              <ProfileReviews
                reviews={profile.reviews}
                stats={profile.stats}
                canWriteReview={canWriteReview}
                onWriteReview={openReview}
              />

              <Divider />

              <ProfilePreferredCourts
                venues={profile.venues}
                isViewerProfile={isViewerProfile}
                onEdit={openEditor(ProfileFormModalName.PreferredCourts)}
              />
            </div>
          </div>

          {/* The aside stretches the row so the card inside it has somewhere to
              travel; sticking the aside itself would pin it with zero range. */}
          {!isEmbedded && (
            <aside className="hidden xl:block xl:pt-ds-3xl">
              <div className="sticky top-24 rounded-3xl border border-color-border-card-lightmode bg-color-bg-lightmode-primary p-ds-xl shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] dark:border-color-border-card-darkmode dark:bg-color-bg-darkmode-primary">
                {actions}
              </div>
            </aside>
          )}
        </div>
      </div>

      {isViewerProfile && (
        <>
          <input
            ref={coverInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            aria-label="Upload a cover photo"
            onChange={(event) => {
              handleCoverFile(event.target.files?.[0]);
              // Re-picking the same file after a failure should fire change again.
              event.target.value = '';
            }}
          />
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            aria-label="Upload a profile photo"
            onChange={(event) => {
              handleAvatarFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </>
      )}

      {CropperModal}

      <ModalShare
        isOpen={isShareOpen}
        closeModal={closeShare}
        shareUrl={shareUrl}
        title="Share profile"
      />

      {isViewerProfile && (
        <>
          <AboutMeFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.AboutMe)}
            onClose={closeFormModal}
            defaultValues={editor.values.profileBasics}
            onSave={editor.saveAboutMe}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <SocialLinksFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.SocialLinks)}
            onClose={closeFormModal}
            defaultValues={editor.values.profileBasics?.socialLinks ?? null}
            onSave={editor.saveSocialLinks}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <PhotosFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.Photos)}
            onClose={closeFormModal}
            defaultPhotos={editor.values.photos}
            onSave={editor.saveGalleryPhotos}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <PlayingLevelFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.PlayingLevel)}
            onClose={closeFormModal}
            defaultValues={editor.values.playingLevel}
            onSave={editor.savePlayingLevel}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <PreferredTimeFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.PreferredTime)}
            onClose={closeFormModal}
            defaultValues={editor.values.preferredCourts}
            onSave={editor.saveAvailabilityWindows}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <PreferredCourtsFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.PreferredCourts)}
            onClose={closeFormModal}
            defaultValues={editor.values.preferredCourts}
            onSave={editor.savePreferredVenuesList}
            isSaving={editor.isSaving}
            error={editor.error}
          />

          <VerificationFormModal
            isOpen={isFormModalOpen(ProfileFormModalName.Verification)}
            isVerified={!!profile.verificationLevel}
            onClose={() => {
              closeFormModal();
              refetch();
            }}
            onVerified={refetch}
          />
        </>
      )}

      <ModalSubmitPlayerReview
        isOpen={isReviewOpen}
        closeModal={closeReview}
        player={{
          id: profile.id,
          name: profile.name,
          profileImagePath: profile.profileImagePath,
        }}
        // Refetch so the new review, the counts and the now-spent gate all update.
        onSubmitted={refetch}
      />
    </>
  );
}
